import {
  TABLE_TOOLBAR_BUTTON_CLASSNAME,
  TABLE_TOOLBAR_LEADING_ICON_CLASSNAME,
  TABLE_TOOLBAR_SEARCH_ICON_CLASSNAME,
  TABLE_TOOLBAR_SEARCH_INPUT_CLASSNAME,
} from "@src/lib/utils/tableToolbarChrome";
import { Button } from "@src/lib/ui/Button";
import MultiSelectBanner, {
  MultiSelectAction,
} from "@src/lib/ui/MultiSelectBanner";
import { TableSkeleton } from "@src/lib/ui/TableSkeleton";
import Text, { TextOverrideProps } from "@src/lib/ui/Text";
import { Input } from "@src/lib/ui/Input";
import { useScreenDimensions } from "@src/lib/hooks/useScreenDimensions";
import useHover from "@src/lib/hooks/useHover";
import { CsvCell, downloadCSV } from "@src/lib/utils/csv";
import { addMissingVisibleColumnsToExportConfigUsingReactTextExtraction } from "@src/lib/utils/tableExportUtils";
import Fuse from "fuse.js";
import { orderBy } from "lodash";
import React, {
  CSSProperties,
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { useTableWithSidebar } from "@src/features/sidebars/hooks/useTableWithDetailSidebar";
import { ViewportList } from "react-viewport-list";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import NextLink from "next/link";
import { createPortal, flushSync } from "react-dom";
import { Route } from "nextjs-routes";
import { isNotNullOrUndefined } from "@src/lib/utils/typeguards";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import DotsIcon from "@src/lib/icons/18px/dots";
import CircleQuestionOutlineIcon from "@src/lib/icons/18px/circle-question-outline";
import ArrowDownIcon from "@src/lib/icons/18px/arrow-down";
import ArrowUpIcon from "@src/lib/icons/18px/arrow-up";
import { Checkbox } from "@src/lib/ui/Checkbox";
import { ColumnSelector } from "@src/lib/ui/ColumnSelector";
import { useColumnSelection } from "@src/features/app/hooks/useColumnSelection";
import { cn } from "@src/lib/utils/cn";
import SearchIcon from "@src/lib/icons/18px/search";
import {
  getDefaultTableColumnWidth,
  getTableColumnOuterWidth,
  DEFAULT_TABLE_COLUMN_WIDTH,
  MIN_TABLE_COLUMN_WIDTH,
  useColumnWidths,
} from "@src/lib/hooks/useColumnWidths";
import {
  computeTableColumnSizing,
  getHorizontalScrollTableMinWidth,
  getTableColumnFlexStyle,
  LEADING_ROW_GUTTER,
  OVERFLOW_MENU_COLUMN_WIDTH,
  TableColumnFlexSizing,
} from "@src/lib/ui/tableColumnSizing";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";

export type RowHeight =
  | number
  | { wrap: true; minHeight: number; maxHeight: number };
export interface TableProps<T, K extends string> {
  title?: string | React.ReactNode;
  allItems: T[];
  allColumns: K[];
  itemId: (item: T) => string | number;
  visibleColumns?: () => K[];
  hiddenColumns?: () => K[];
  columnTitles?: Record<K, string>;
  renderFns: RenderFns<T, K>;
  headerCellStyles?: HeaderCellStyles<K>;
  responsiveness?: ResponsivenessConfigs<K>;
  sortingFns?: SortingFns<T, K>;
  multiselect?: MultiSelectConfig<T>;
  filterItems?: (allItems: T[]) => T[];
  defaultSort?: SortConfig<K>;
  rowStyles?: CSSProperties;
  rowHeight?: RowHeight;
  onRowClick?: (item: T, e: React.MouseEvent<HTMLDivElement>) => void;
  /** Fires when the pointer enters a row — a place to warm caches for the
      page the row opens, ahead of the click. */
  onRowHover?: (item: T) => void;
  rowClickLink?: (item: T) => Route | undefined | null;
  rowLinkOpenInNewTab?: boolean;
  disableRowClickOnColumns?: Set<K>;
  overflowMenu?: (item: T) => React.ReactNode;
  fuseSearchConfig?: FuseSearchConfig<T>;
  rightAction?: React.ReactNode;
  loading?: boolean;
  headerStickyPositioning?: number;
  noDataEmptyState?: React.ReactNode;
  exportConfig?: ExportConfig<T, K>;
  showCount?: boolean;
  SubHeader?: React.ReactNode;
  disabledSorts?: K[];
  emptyStatePaddingTop?: number;
  activeColumn?: K;
  onColumnHeaderClick?: (column: K) => void;
  headerHeight?: number;
  dataTestId?: string;
  sidebarSelectedRowId?: string | number;
  sidebarConfig?: {
    tableId: string;
    sidebarType: DetailSidebarType;
    enabled: boolean;
  };
  columnSelection?:
    | {
        enabled: boolean;
        alwaysVisibleColumns?: K[];
        defaultSelectedColumns?: K[];
      }
    | boolean; // shorthand for { enabled: true }
  /**
   * Horizontal-scroll layout. Each column's width (`useColumnWidths`: the
   * user's dragged width, else `getDefaultTableColumnWidth`: width ?? minWidth
   * ?? 160, the first column 320 with its `width` capped at 320) is its
   * minimum; when the container is wider, data columns grow to
   * fill it (`responsiveness.grow` / `maxWidth`, `growableColumn`; see
   * `computeTableColumnSizing`). Below the sum of widths the table scrolls.
   */
  horizontalScroll?: boolean;
  /** Keep the first visible data column (and checkbox column) sticky while scrolling horizontally. */
  stickyFirstColumn?: boolean;
  /** Show drag handles on column headers to resize widths. */
  resizableColumns?: boolean;
  /** Key for `useColumnWidths` localStorage persistence; omit for session-only widths. */
  columnWidthPersistenceKey?: string;
  /** Reserve this many px of fixed empty space after the last column in horizontal-scroll mode. */
  trailingEmptyColumnWidth?: number;
  /**
   * `horizontalScroll` shorthand: only this column grows into leftover width
   * (others grow only with an explicit `responsiveness.grow`). Works on a
   * sticky first column. Once the user resizes it, it is fixed like any
   * resized column and the last data column absorbs the slack.
   */
  growableColumn?: K;
  /**
   * Optional DOM node to portal the filter row (title/count + search +
   * column selector + export) into, e.g. `#top-nav-subheader`. Same pattern as
   * {@link PaginatedTable}.
   */
  filterBarContainer?: HTMLElement | null;
  /**
   * When true, the data portion of the table (header row + data rows + its
   * horizontal-scroll container) is pulled back to the edges of the nearest
   * `px-6` ancestor with negative horizontal margin. The toolbar / filter bar
   * remain within the padded column so only the rows run edge-to-edge. Same
   * pattern as {@link PaginatedTable}.
   */
  fullBleedTable?: boolean;
  /**
   * The table reserves room below its last row so a page-level scroll doesn't
   * end flush against the viewport. Turn it off where the table is a section
   * inside a card, where that room reads as the card being half empty.
   */
  bottomSpacer?: boolean;
}

export default function Table<T, K extends string>({
  title,
  allItems,
  allColumns,
  itemId,
  visibleColumns,
  hiddenColumns,
  columnTitles,
  renderFns,
  responsiveness,
  sortingFns,
  filterItems,
  defaultSort,
  multiselect,
  rowStyles,
  rowHeight = 42,
  onRowClick,
  onRowHover,
  rowClickLink,
  rowLinkOpenInNewTab,
  overflowMenu,
  fuseSearchConfig,
  rightAction,
  loading,
  headerStickyPositioning,
  disableRowClickOnColumns,
  noDataEmptyState,
  exportConfig,
  showCount = true,
  SubHeader,
  disabledSorts,
  emptyStatePaddingTop = 80,
  activeColumn,
  headerCellStyles,
  onColumnHeaderClick,
  headerHeight,
  dataTestId,
  sidebarSelectedRowId,
  sidebarConfig,
  columnSelection,
  horizontalScroll = false,
  stickyFirstColumn = false,
  resizableColumns = false,
  columnWidthPersistenceKey,
  trailingEmptyColumnWidth,
  growableColumn,
  filterBarContainer,
  fullBleedTable = false,
  bottomSpacer = true,
}: TableProps<T, K>): React.ReactElement {
  const [selectedSort, setSelectedSort] = useState<SortConfig<K> | undefined>(
    defaultSort,
  );
  // Use scoped query parameter for sidebar tables to avoid conflicts with main page tables
  const searchQueryParamName = sidebarConfig?.tableId
    ? `q-${sidebarConfig.tableId}`
    : "q";
  const [searchValue, setSearchValue] = useQueryParam(
    searchQueryParamName,
    withDefault(StringParam, ""),
  );
  let items = allItems;
  items = useMemo(() => {
    if (!sortingFns || !selectedSort) {
      return items;
    }
    const sortFn = sortingFns[selectedSort.column];
    if (!sortFn) {
      return items;
    }
    let sortedItems = orderBy(items, sortFn);
    const sortValuesToPutOnBottom: Set<SortFnReturnValue> = new Set([
      undefined,
      null,
      "",
    ]);
    const bottomItems = sortedItems.filter((x) =>
      sortValuesToPutOnBottom.has(sortFn(x)),
    );
    let topItems = sortedItems.filter(
      (x) => !sortValuesToPutOnBottom.has(sortFn(x)),
    );
    if (selectedSort.descending) {
      topItems = topItems.reverse();
    }
    sortedItems = [...topItems, ...bottomItems];
    return sortedItems;
  }, [items, sortingFns, selectedSort]);
  items = useMemo(() => {
    if (filterItems) {
      return filterItems(items);
    }
    return items;
  }, [filterItems, items]);
  items = useMemo(() => {
    if (searchValue && fuseSearchConfig) {
      return new Fuse(items, fuseSearchConfig)
        .search(searchValue)
        .map((m) => m.item);
    } else {
      return items;
    }
  }, [fuseSearchConfig, items, searchValue]);

  // Internal sidebar management (when sidebarConfig is provided)
  // Always call the hook but conditionally use its results
  const internalSidebarState = useTableWithSidebar({
    tableId: sidebarConfig?.tableId || "disabled",
    items: sidebarConfig?.enabled ? items : [], // Use empty array when disabled
    itemId: (item: T) => String(itemId(item)),
    sidebarType: sidebarConfig?.sidebarType || DetailSidebarType.User,
  });

  // Use internal sidebar state if configured, otherwise use external props
  const finalOnRowClick = sidebarConfig?.enabled
    ? internalSidebarState.onRowClick
    : onRowClick;
  const finalSidebarSelectedRowId = sidebarConfig?.enabled
    ? internalSidebarState.sidebarSelectedRowId
    : sidebarSelectedRowId;

  // Normalize columnSelection config
  const normalizedColumnSelection = useMemo(() => {
    if (typeof columnSelection === "boolean") {
      return columnSelection ? { enabled: true } : { enabled: false };
    }
    return columnSelection;
  }, [columnSelection]);

  // Column selection state management
  const {
    selectedColumns: userSelectedColumns,
    setSelectedColumns: setUserSelectedColumns,
    visibleColumnsFunction: dynamicVisibleColumns,
  } = useColumnSelection({
    allColumns,
    defaultSelectedColumns:
      normalizedColumnSelection?.defaultSelectedColumns || allColumns,
    key: sidebarConfig?.tableId || dataTestId, // Use tableId or dataTestId as unique key
  });

  // Use either the provided visibleColumns function or the dynamic one
  const finalVisibleColumns = useMemo(() => {
    if (normalizedColumnSelection?.enabled) {
      return dynamicVisibleColumns;
    }
    return visibleColumns;
  }, [
    normalizedColumnSelection?.enabled,
    dynamicVisibleColumns,
    visibleColumns,
  ]);

  const [lastSelectedItemId, setLastSelectedItemId] = useState<KeyType>();
  const [allFilteredItemsSelected, setAllFilteredItemsSelected] =
    useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<KeyType>>(
    new Set(),
  );
  useEffect(() => {
    setSelectedItemIds((prevSelectedItemIds) => {
      const validIds = new Set(allItems.map((item) => itemId(item)));
      return new Set([...prevSelectedItemIds].filter((id) => validIds.has(id)));
    });
  }, [allItems, itemId]);
  const toggleItemSelected = useCallback(
    (item: T, holdingShift: boolean) => {
      setLastSelectedItemId(itemId(item));
      setSelectedItemIds((oldState) => {
        const newState = new Set(oldState);
        if (newState.has(itemId(item))) {
          newState.delete(itemId(item));
          setAllFilteredItemsSelected(false);
        } else {
          newState.add(itemId(item));
        }
        return newState;
      });
      if (!holdingShift) {
        return;
      }
      const lastSelectedItemIndex = items.findIndex((x) => {
        return itemId(x) === lastSelectedItemId;
      });
      const toggledItemIndex = items.findIndex((x) => {
        return itemId(x) === itemId(item);
      });
      if (lastSelectedItemIndex === -1 || toggledItemIndex === -1) {
        return;
      }
      const min = Math.min(lastSelectedItemIndex, toggledItemIndex);
      const max = Math.max(lastSelectedItemIndex, toggledItemIndex);
      const newSelectedItemIds = new Set(selectedItemIds);
      items.slice(min, max + 1).forEach((x) => {
        if (selectedItemIds.has(itemId(item))) {
          newSelectedItemIds.delete(itemId(x));
        } else {
          newSelectedItemIds.add(itemId(x));
        }
      });
      setSelectedItemIds(newSelectedItemIds);
    },
    [itemId, items, lastSelectedItemId, selectedItemIds],
  );
  const toggleAllFilteredItemsSelected = useCallback(() => {
    setAllFilteredItemsSelected(!allFilteredItemsSelected);
    if (allFilteredItemsSelected) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(
        new Set(
          items
            .filter((x) => {
              return !(
                multiselect?.disableMultiSelectForItem &&
                multiselect.disableMultiSelectForItem(x)
              );
            })
            .map((x) => itemId(x)),
        ),
      );
    }
  }, [allFilteredItemsSelected, itemId, items, multiselect]);
  const deselectAllSelectedItems = useCallback(() => {
    setSelectedItemIds(new Set());
  }, [setSelectedItemIds]);
  const { width: screenWidth } = useScreenDimensions();
  const columnsToDisplay: K[] = useMemo(
    () =>
      (finalVisibleColumns?.() || allColumns).filter((x) => {
        if (hiddenColumns?.().includes(x)) return false;
        const columnPageMinWidth =
          responsiveness?.[x]?.collapseAtScreenWidth || 0;
        return screenWidth > columnPageMinWidth;
      }),
    [
      finalVisibleColumns,
      allColumns,
      hiddenColumns,
      responsiveness,
      screenWidth,
    ],
  );
  const defaultColumnWidths = useMemo(() => {
    const w = {} as Record<K, number>;
    for (const c of allColumns) {
      w[c] = getDefaultTableColumnWidth(
        c === columnsToDisplay[0],
        responsiveness?.[c]?.width,
        responsiveness?.[c]?.minWidth,
      );
    }
    return w;
  }, [allColumns, columnsToDisplay, responsiveness]);

  const columnMinWidths = useMemo(() => {
    const m = {} as Partial<Record<K, number>>;
    for (const c of allColumns) {
      if (responsiveness?.[c]?.minWidth != null) {
        m[c] = responsiveness[c].minWidth!;
      }
    }
    return m;
  }, [allColumns, responsiveness]);

  const {
    widths: columnWidths,
    resizedColumns,
    commitWidth: commitColumnWidth,
  } = useColumnWidths(
    horizontalScroll ? columnWidthPersistenceKey : undefined,
    allColumns,
    defaultColumnWidths,
    columnMinWidths,
    // A sticky first column never grew before :v4, so its saved width was a
    // real width, not a growth floor; keep it.
    growableColumn === columnsToDisplay[0] && stickyFirstColumn
      ? undefined
      : growableColumn,
  );

  const columnSizing = useMemo(
    () =>
      horizontalScroll
        ? computeTableColumnSizing({
            columns: columnsToDisplay,
            widths: columnWidths,
            responsiveness,
            growableColumn,
            resizedColumns,
          })
        : undefined,
    [
      horizontalScroll,
      columnsToDisplay,
      columnWidths,
      responsiveness,
      growableColumn,
      resizedColumns,
    ],
  );

  const totalTableMinWidth = useMemo(
    () =>
      horizontalScroll
        ? getHorizontalScrollTableMinWidth({
            columnBases: columnsToDisplay.map((col) =>
              getTableColumnOuterWidth(columnWidths[col]),
            ),
            checkboxColumnWidth: multiselect?.checkboxColumnWidth,
            hasOverflowMenu: !!overflowMenu,
            trailingEmptyColumnWidth,
          })
        : undefined,
    [
      horizontalScroll,
      multiselect,
      columnsToDisplay,
      columnWidths,
      overflowMenu,
      trailingEmptyColumnWidth,
    ],
  );

  const checkboxOuterWidth = useMemo(
    () =>
      multiselect != null
        ? getTableColumnOuterWidth(multiselect.checkboxColumnWidth)
        : LEADING_ROW_GUTTER,
    [multiselect],
  );

  const tableHScrollRef = useRef<HTMLDivElement>(null);
  const [stickyScrollEdgeShadow, setStickyScrollEdgeShadow] = useState(false);
  const syncStickyScrollShadow = useCallback(() => {
    const el = tableHScrollRef.current;
    if (!el) return;
    setStickyScrollEdgeShadow(el.scrollLeft > 0);
  }, []);

  useEffect(() => {
    if (!horizontalScroll || !stickyFirstColumn) {
      setStickyScrollEdgeShadow(false);
      return;
    }
    const el = tableHScrollRef.current;
    if (!el) return;
    syncStickyScrollShadow();
    el.addEventListener("scroll", syncStickyScrollShadow, { passive: true });
    window.addEventListener("resize", syncStickyScrollShadow);
    return () => {
      el.removeEventListener("scroll", syncStickyScrollShadow);
      window.removeEventListener("resize", syncStickyScrollShadow);
    };
  }, [
    horizontalScroll,
    stickyFirstColumn,
    syncStickyScrollShadow,
    totalTableMinWidth,
    columnsToDisplay,
  ]);

  const showLoadingState = useMemo(
    () => !!loading && allItems.length === 0,
    [allItems.length, loading],
  );
  const showNoSearchResultsEmptyState = useMemo(() => {
    return items.length === 0 && allItems.length !== 0 && !showLoadingState;
  }, [allItems.length, items.length, showLoadingState]);
  const selectedItems = useMemo(
    () => allItems.filter((x) => selectedItemIds.has(itemId(x))),
    [allItems, itemId, selectedItemIds],
  );
  return (
    <div
      className="flex flex-col self-stretch"
      data-testid={dataTestId || "table"}
    >
      {(showCount ||
        fuseSearchConfig ||
        rightAction ||
        exportConfig ||
        normalizedColumnSelection?.enabled) &&
        (() => {
          const searchControl = fuseSearchConfig ? (
            <div className="flex w-[200px] max-w-[200px]">
              <div className="relative w-full">
                <SearchIcon className={TABLE_TOOLBAR_SEARCH_ICON_CLASSNAME} />
                <Input
                  placeholder="Search..."
                  aria-label="Search"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  inputClassName={TABLE_TOOLBAR_SEARCH_INPUT_CLASSNAME}
                />
              </div>
            </div>
          ) : null;
          const columnSelectorControl = normalizedColumnSelection?.enabled ? (
            <ColumnSelector
              allColumns={allColumns}
              selectedColumns={userSelectedColumns}
              onSelectionChange={setUserSelectedColumns}
              columnTitles={columnTitles}
              alwaysVisibleColumns={
                normalizedColumnSelection.alwaysVisibleColumns
              }
              defaultSelectedColumns={
                normalizedColumnSelection.defaultSelectedColumns || allColumns
              }
              className="w-64"
            />
          ) : null;
          const filterBar = (
            <div
              className={cn(
                "flex items-center justify-between self-stretch",
                filterBarContainer && "order-last min-w-0 w-full flex-1",
              )}
            >
              <div className="flex items-center">
                {filterBarContainer && searchControl}
                {title && (
                  <div className="mr-2 flex">
                    {typeof title === "string" ? (
                      <Text variant="body-title">{title}</Text>
                    ) : (
                      title
                    )}
                  </div>
                )}
                {showLoadingState ? (
                  <div />
                ) : title && showCount ? (
                  <TableItemCount
                    count={items.length}
                    totalCount={allItems.length}
                  />
                ) : null}
                {filterBarContainer && columnSelectorControl}
              </div>
              <div className="flex gap-2">
                {!filterBarContainer && searchControl}
                {!filterBarContainer && columnSelectorControl}
                {rightAction}
                {exportConfig && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className={TABLE_TOOLBAR_BUTTON_CLASSNAME}
                    onClick={() => {
                      const currentVisibleColumns =
                        finalVisibleColumns?.() || allColumns;

                      const enhancedConfig =
                        addMissingVisibleColumnsToExportConfigUsingReactTextExtraction(
                          exportConfig,
                          currentVisibleColumns,
                          renderFns,
                          columnTitles,
                        );

                      downloadCSV(
                        enhancedConfig.title,
                        enhancedConfig.combinedColumnTitles,
                        items.map((i) => {
                          return enhancedConfig.allExportColumns.map((c) =>
                            enhancedConfig.combinedExportFns[c](i),
                          );
                        }),
                      );
                    }}
                  >
                    <ArrowDownloadIcon
                      className={TABLE_TOOLBAR_LEADING_ICON_CLASSNAME}
                    />
                    Export
                  </Button>
                )}
              </div>
            </div>
          );
          return filterBarContainer
            ? createPortal(filterBar, filterBarContainer)
            : filterBar;
        })()}
      {SubHeader}
      <div
        ref={tableHScrollRef}
        className={cn(
          "flex flex-col self-stretch",
          horizontalScroll && "overflow-x-auto overflow-y-visible",
          fullBleedTable && "-mx-6",
        )}
      >
        <div
          data-table-inner={horizontalScroll ? "true" : undefined}
          className={cn(
            "flex min-w-0 flex-col",
            horizontalScroll ? "self-start" : "self-stretch",
          )}
          style={
            // Full width so the data columns can absorb the slack; below the
            // sum of column widths the min width makes the container scroll.
            horizontalScroll && totalTableMinWidth != null
              ? { minWidth: totalTableMinWidth, width: "100%" }
              : undefined
          }
        >
          {columnTitles && (
            <TableHeaderRow
              columns={columnsToDisplay}
              columnTitles={columnTitles}
              responsiveness={responsiveness}
              multiselect={multiselect}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              allFilteredItemsSelected={allFilteredItemsSelected}
              toggleAllFilteredItemsSelected={toggleAllFilteredItemsSelected}
              includeOverflowMenuColumn={!!overflowMenu}
              stickyPositioning={headerStickyPositioning}
              isSkeletonState={showLoadingState}
              disabledSorts={disabledSorts}
              headerCellStyles={headerCellStyles}
              onColumnHeaderClick={onColumnHeaderClick}
              activeColumn={activeColumn}
              headerHeight={headerHeight}
              horizontalScroll={horizontalScroll}
              stickyFirstColumn={stickyFirstColumn}
              resizableColumns={resizableColumns}
              columnWidths={horizontalScroll ? columnWidths : undefined}
              onColumnResizeCommit={
                horizontalScroll ? commitColumnWidth : undefined
              }
              checkboxOuterWidth={checkboxOuterWidth}
              trailingEmptyColumnWidth={
                horizontalScroll ? trailingEmptyColumnWidth : undefined
              }
              columnSizing={columnSizing}
              stickyScrollEdgeShadow={stickyScrollEdgeShadow}
            />
          )}
          <ViewportList
            items={items}
            itemMinSize={typeof rowHeight === "number" ? rowHeight : undefined}
          >
            {(item) => {
              return (
                <TableContentRow
                  key={itemId(item)}
                  item={item}
                  columns={columnsToDisplay}
                  renderFns={renderFns}
                  multiselect={multiselect}
                  responsiveness={responsiveness}
                  isSelected={selectedItemIds.has(itemId(item))}
                  toggleItemSelected={toggleItemSelected}
                  rowStyles={rowStyles}
                  rowHeight={rowHeight}
                  onRowClick={finalOnRowClick}
                  onRowHover={onRowHover}
                  rowClickLink={rowClickLink}
                  rowLinkOpenInNewTab={rowLinkOpenInNewTab}
                  disableRowClickOnColumns={disableRowClickOnColumns}
                  overflowMenu={overflowMenu}
                  activeColumn={activeColumn}
                  isSidebarSelected={
                    finalSidebarSelectedRowId !== undefined &&
                    String(itemId(item)) === String(finalSidebarSelectedRowId)
                  }
                  horizontalScroll={horizontalScroll}
                  stickyFirstColumn={stickyFirstColumn}
                  columnWidths={horizontalScroll ? columnWidths : undefined}
                  checkboxOuterWidth={checkboxOuterWidth}
                  trailingEmptyColumnWidth={
                    horizontalScroll ? trailingEmptyColumnWidth : undefined
                  }
                  columnSizing={columnSizing}
                  stickyScrollEdgeShadow={stickyScrollEdgeShadow}
                />
              );
            }}
          </ViewportList>
          {showNoSearchResultsEmptyState && (
            <div className="flex justify-center self-stretch pt-20">
              <Text variant="compact-title" className="text-text-tertiary">
                No results
              </Text>
            </div>
          )}
          {showLoadingState && (
            <TableSkeleton
              columns={columnsToDisplay.map((col) => ({
                width: responsiveness?.[col]?.width,
                minWidth: responsiveness?.[col]?.minWidth,
                flex: responsiveness?.[col]?.flex,
                sizing: columnSizing?.[col],
              }))}
              horizontalScroll={horizontalScroll}
              trailingEmptyColumnWidth={trailingEmptyColumnWidth}
              rowHeight={
                typeof rowHeight === "number"
                  ? rowHeight
                  : rowHeight?.minHeight ?? 42
              }
              showOverflowColumn={!!overflowMenu}
              showCheckboxColumn={!!multiselect}
              checkboxColumnWidth={multiselect?.checkboxColumnWidth}
            />
          )}
          {!showLoadingState && allItems.length === 0 && (
            <div
              className="flex justify-center self-stretch"
              style={{ paddingTop: emptyStatePaddingTop }}
            >
              {!noDataEmptyState || typeof noDataEmptyState === "string" ? (
                <Text variant="compact-title" className="text-text-tertiary">
                  {noDataEmptyState || "Nothing to display"}
                </Text>
              ) : (
                noDataEmptyState
              )}
            </div>
          )}
        </div>
      </div>
      {multiselect && (
        <MultiSelectBanner
          numSelected={selectedItemIds.size}
          onDeselectAll={deselectAllSelectedItems}
          actions={multiselect.actions(selectedItems, deselectAllSelectedItems)}
          destroyLabel={multiselect.destroyLabel}
          onDestroy={
            multiselect.onDestroy
              ? () => {
                  if (multiselect.onDestroy) {
                    multiselect.onDestroy(
                      selectedItems,
                      deselectAllSelectedItems,
                    );
                  }
                }
              : undefined
          }
        />
      )}
      {bottomSpacer && <div className="h-28 w-full" />}
    </div>
  );
}

export type RenderFns<T, K extends string> = Record<
  K,
  (item: T) => React.ReactNode
>;

export type HeaderCellStyles<K extends string> = Record<
  K,
  { inner?: CSSProperties; outer?: CSSProperties }
>;

export type ExportConfig<T, K extends string> = {
  title: string;
  exportColumns: () => readonly K[];
  exportColumnTitles?: Record<K, string>;
  exportFns: Record<K, (item: T) => CsvCell>;
};

export type HeaderTooltipCopy<K extends string> = Partial<Record<K, string>>;

export type SortConfig<K extends string> = {
  column: K;
  descending: boolean;
  nullsLast?: boolean;
};

export type ResponsivenessConfigs<K extends string> = Partial<
  Record<K, ResponsivenessConfig>
>;

export type SortingFns<T, K extends string> = Partial<
  Record<K, (item: T) => SortFnReturnValue>
>;

type SortFnReturnValue = string | number | undefined | null | boolean;

export type ResponsivenessConfig = {
  /**
   * Inner width in px. In `horizontalScroll` mode it is the column's default
   * flex basis / minimum (until the user resizes it); otherwise a fixed width.
   */
  width?: number;
  minWidth?: number;
  /**
   * Non-scroll layout: CSS `flex`. In `horizontalScroll` mode a numeric value
   * is used as the grow weight when `grow` isn't set.
   */
  flex?: CSSProperties["flex"];
  /**
   * `horizontalScroll` only: relative share of leftover width (1 = a default
   * 160px column; 0 = fixed). Defaults to proportional to `width`. Ignored for
   * other columns when the table sets `growableColumn`, unless set here.
   */
  grow?: number;
  /** `horizontalScroll` only: inner width cap when growing. */
  maxWidth?: number;
  collapseAtScreenWidth?: number;
};
export type MultiSelectConfig<T> = {
  checkboxColumnWidth: number;
  renderAvatarFn: (item: T) => React.ReactNode;
  actions: (selectedItems: T[], deselectAll: () => void) => MultiSelectAction[];
  disableMultiSelectForItem?: (item: T) => boolean;
  onDestroy?: (selectedItems: T[], deselectAll: () => void) => void;
  hasDestroyPermission?: boolean;
  destroyLabel?: string;
};

export type RowConfig<T> = {
  onClick?: (item: T) => void;
  height?: "42px" | "56px";
  styles?: CSSProperties;
};

export type KeyType = string | number;

export type FuseSearchConfig<T> = {
  threshold?: number;
  keys: FuseSearchKey<T>[];
};

export type FuseSearchKey<T> = {
  name: string;
  getFn: (item: T) => string;
};

export const TableItemCount: FC<{
  loading?: boolean;
  count?: number;
  totalCount?: number;
  /**
   * Renders as labelled prose ("Showing 10 of 20 items") instead of a bare
   * number in a chip. Use wherever the count stands alone with no title beside
   * it — a lone "10 of 20" there reads as a stray number.
   */
  labelled?: boolean;
}> = (props) => {
  const textValue = useMemo(() => {
    if (props.loading && !props.totalCount) {
      return "";
    }
    const formatCount = (value: number | undefined) =>
      value != null ? value.toLocaleString("en-US") : value;
    const filtered = props.count !== props.totalCount;
    if (props.labelled) {
      const noun = props.totalCount === 1 ? "item" : "items";
      return filtered
        ? `Showing ${formatCount(props.count)} of ${formatCount(props.totalCount)} ${noun}`
        : `${formatCount(props.totalCount)} ${noun}`;
    }
    if (filtered) {
      return `${formatCount(props.count)} of ${formatCount(props.totalCount)}`;
    } else {
      return formatCount(props.totalCount);
    }
  }, [props.count, props.labelled, props.loading, props.totalCount]);
  if (props.labelled) {
    return (
      <Text variant="fine-print-title" className="text-text-tertiary">
        {textValue}
      </Text>
    );
  }
  return (
    <div
      className="flex items-center justify-center rounded-md bg-state-hover p-1"
      style={{
        height: 20,
        minWidth: 16,
      }}
    >
      <Text variant="fine-print-title" className="text-text-tertiary">
        {textValue}
      </Text>
    </div>
  );
};

export type TableContentCellProps = {
  children?: React.ReactNode;
  width?: number;
  minWidth?: number;
  alignContent?: "Left" | "Right" | "Center";
  className?: string;
  innerClassName?: string;
  innerStyle?: CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  linkUrl?: Route | null | undefined;
  linkOpenInNewTab?: boolean;
  flex?: CSSProperties["flex"];
  style?: CSSProperties;
  wrapHeight?: boolean;
  defaultEmptyTextToDash?: boolean;
  active?: boolean;
  textProps?: TextOverrideProps;
  /** When set, cell sticks horizontally inside a scroll container. */
  stickyLeft?: number;
  /** When set, cell sticks to the right edge inside a scroll container. */
  stickyRight?: number;
  /**
   * Keep the cell stuck in place without painting a surface or edge line, so
   * scrolling content shows through. Children supply their own background.
   */
  stickyTransparent?: boolean;
  stickyZIndex?: number;
  /** Solid surface behind sticky cells (matches row selection / hover). */
  stickySurfaceClassName?: string;
  /** Horizontal-scroll layout: no shrinking; fixed at `width` unless `flexSizing` is set. */
  layoutFixed?: boolean;
  /**
   * With `layoutFixed`, sizes the cell as `flex: <grow> 0 <basis>px` with
   * `min-width: <basis>px` (see `computeTableColumnSizing`), overriding
   * `width` / `minWidth`. Header, body and skeleton cells of one column get
   * the same value so they stay aligned.
   */
  flexSizing?: TableColumnFlexSizing;
  /** e.g. column resize hit target, rendered inside the outer cell. */
  suffix?: React.ReactNode;
  /** Column key emitted as `data-column` for resize drag-handlers to find cells. */
  dataColumn?: string;
  /** When sticky, add a soft right-edge shadow (table scrolled horizontally). */
  stickyScrollEdgeShadow?: boolean;
  /** When sticky right, add a soft left-edge shadow (table can scroll further right). */
  stickyRightScrollEdgeShadow?: boolean;
};

const PADDING_LEFT = 8;

// Opaque versions of the row state fills, for sticky cells.
const STICKY_SELECTED_SURFACE =
  "bg-[linear-gradient(var(--color-state-selected),var(--color-state-selected))]";
const STICKY_SIDEBAR_SELECTED_SURFACE =
  "bg-[linear-gradient(var(--color-state-pressed),var(--color-state-pressed))]";
const STICKY_HOVER_SURFACE =
  "group-hover/row:bg-[linear-gradient(var(--color-state-hover),var(--color-state-hover))]";

export { LEADING_ROW_GUTTER, OVERFLOW_MENU_COLUMN_WIDTH };

export const TableContentCell = forwardRef<
  HTMLDivElement,
  TableContentCellProps
>(
  (
    {
      flex = 1,
      children,
      width,
      minWidth,
      alignContent = "Left",
      className,
      innerClassName,
      style,
      onClick,
      linkUrl,
      linkOpenInNewTab,
      wrapHeight,
      defaultEmptyTextToDash = true,
      active,
      innerStyle,
      textProps,
      stickyLeft,
      stickyRight,
      stickyTransparent,
      stickyZIndex,
      stickySurfaceClassName,
      layoutFixed,
      flexSizing,
      suffix,
      dataColumn,
      stickyScrollEdgeShadow,
      stickyRightScrollEdgeShadow,
    },
    ref,
  ) => {
    const childrenToRender = useMemo(() => {
      if (
        typeof children === "string" ||
        typeof children === "number" ||
        children === undefined ||
        children === null ||
        children === false
      ) {
        return (
          // Legacy typography: callers override via TextOverrideProps
          // (type/fontWeight).
          <Text
            type={"P2"}
            ellipsis
            ellipsisParentFlexDirection={"row"}
            {...textProps}
          >
            {isNotNullOrUndefined(children)
              ? children
              : defaultEmptyTextToDash
                ? "-"
                : undefined}
          </Text>
        );
      }
      return children;
    }, [children, defaultEmptyTextToDash, textProps]);
    const innerPositioningStyles: CSSProperties = useMemo(
      () => ({
        position: wrapHeight ? undefined : "absolute",
        overflow: "hidden",
        minWidth: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "row",
        alignSelf: "stretch",
        flex: 1,
        alignItems: "center",
        paddingLeft: PADDING_LEFT,
        ...innerStyle,
      }),
      [wrapHeight, innerStyle],
    );
    const stickyInnerBackdrop =
      !stickyTransparent &&
      (stickyLeft !== undefined || stickyRight !== undefined)
        ? stickySurfaceClassName ?? "bg-bg-primary"
        : undefined;
    const innerComponent = useMemo(() => {
      if (linkUrl) {
        return (
          <NextLink
            href={linkUrl}
            target={linkOpenInNewTab ? "_blank" : undefined}
            id={"TableContentCell-inner"}
            style={{
              ...innerPositioningStyles,
              textDecoration: "none",
            }}
            className={cn(
              alignContent === "Left" && "justify-start",
              alignContent === "Center" && "justify-center",
              alignContent === "Right" && "justify-end",
              stickyInnerBackdrop,
              active && "bg-yellow-2",
              "cursor-pointer hover:underline [text-decoration-color:var(--color-text-tertiary)]",
              innerClassName,
            )}
          >
            {childrenToRender}
          </NextLink>
        );
      } else {
        return (
          <div
            id={"TableContentCell-inner"}
            style={innerPositioningStyles}
            className={cn(
              alignContent === "Left" && "justify-start",
              alignContent === "Center" && "justify-center",
              alignContent === "Right" && "justify-end",
              stickyInnerBackdrop,
              active && "bg-yellow-2",
              innerClassName,
            )}
          >
            {childrenToRender}
          </div>
        );
      }
    }, [
      alignContent,
      childrenToRender,
      innerPositioningStyles,
      innerClassName,
      linkUrl,
      linkOpenInNewTab,
      active,
      stickyInnerBackdrop,
    ]);
    const sizing = layoutFixed ? flexSizing : undefined;
    const fixedWidth = width ? width + PADDING_LEFT : undefined;
    const sizingStyles: CSSProperties = sizing
      ? getTableColumnFlexStyle(sizing)
      : layoutFixed
        ? {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: "auto",
            width: fixedWidth,
            minWidth: minWidth ? minWidth + PADDING_LEFT : PADDING_LEFT,
            maxWidth: fixedWidth,
          }
        : {
            flex,
            width: fixedWidth,
            minWidth: minWidth ? minWidth + PADDING_LEFT : PADDING_LEFT,
            maxWidth: fixedWidth,
          };
    return (
      <div
        ref={ref}
        data-column={dataColumn}
        data-grow={sizing && sizing.grow > 0 ? "true" : undefined}
        className={cn(
          className,
          layoutFixed &&
            stickyLeft === undefined &&
            stickyRight === undefined &&
            "border-r border-border-primary/50",
          stickyLeft !== undefined &&
            cn(
              "isolate",
              stickyScrollEdgeShadow
                ? "shadow-[1px_0_0_0_var(--color-border-primary),6px_0_16px_-6px_var(--color-alpha-dark-8)]"
                : "shadow-[1px_0_0_0_var(--color-border-primary)]",
              stickySurfaceClassName ?? "bg-bg-primary",
            ),
          stickyRight !== undefined &&
            stickyTransparent &&
            "isolate bg-transparent",
          stickyRight !== undefined &&
            !stickyTransparent &&
            cn(
              "isolate",
              stickyRightScrollEdgeShadow
                ? "shadow-[-1px_0_0_0_var(--color-border-primary),-6px_0_16px_-6px_var(--color-alpha-dark-8)]"
                : "shadow-[-1px_0_0_0_var(--color-border-primary)]",
              stickySurfaceClassName ?? "bg-bg-primary",
            ),
          onClick && !linkUrl && "cursor-pointer hover:bg-transparent",
        )}
        onClick={(e) => onClick?.(e)}
        style={{
          position:
            stickyLeft !== undefined || stickyRight !== undefined
              ? "sticky"
              : "relative",
          left: stickyLeft !== undefined ? stickyLeft : undefined,
          right: stickyRight !== undefined ? stickyRight : undefined,
          zIndex:
            stickyLeft !== undefined || stickyRight !== undefined
              ? stickyZIndex ?? 2
              : undefined,
          alignSelf: wrapHeight ? "center" : "stretch",
          ...sizingStyles,
          ...style,
        }}
      >
        {innerComponent}
        {suffix}
      </div>
    );
  },
);

TableContentCell.displayName = "TableContentCell";

function TableRowOverflow({
  menu,
}: {
  menu: React.ReactNode;
  showOverflow: boolean;
}): React.ReactElement {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-md"
          className="size-7 rounded-lg bg-bg-primary shadow-default hover:bg-bg-tertiary aria-expanded:bg-bg-tertiary [&_svg]:size-3.5"
        >
          <DotsIcon className="text-icon-primary" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{menu}</DropdownMenuContent>
    </DropdownMenu>
  );
}

type TableRowProps<T, K extends string> = {
  item: T;
  columns: K[];
  renderFns: RenderFns<T, K>;
  renderFnsTextProps?: Partial<Record<K, TextOverrideProps>>;
  multiselect?: MultiSelectConfig<T>;
  responsiveness?: ResponsivenessConfigs<K>;
  rowConfig?: RowConfig<T>;
  isSelected?: boolean;
  toggleItemSelected?: (item: T, holdingShift: boolean) => void;
  rowStyles?: CSSProperties;
  rowHeight: RowHeight;
  onRowClick?: (item: T, e: React.MouseEvent<HTMLDivElement>) => void;
  /** Fires when the pointer enters a row — a place to warm caches for the
      page the row opens, ahead of the click. */
  onRowHover?: (item: T) => void;
  rowClickLink?: (item: T) => Route | undefined | null;
  rowLinkOpenInNewTab?: boolean;
  overflowMenu?: (item: T) => React.ReactNode;
  disableRowClickOnColumns?: Set<K>;
  isSkeletonState?: boolean;
  activeColumn?: K;
  isSidebarSelected?: boolean;
  horizontalScroll?: boolean;
  stickyFirstColumn?: boolean;
  columnWidths?: Record<K, number>;
  checkboxOuterWidth?: number;
  /** See `TableHeaderRowProps.trailingEmptyColumnWidth`. */
  trailingEmptyColumnWidth?: number;
  /** See `TableHeaderRowProps.columnSizing`. */
  columnSizing?: Partial<Record<K, TableColumnFlexSizing>>;
  stickyScrollEdgeShadow?: boolean;
};

export function TableContentRow<T, K extends string>({
  item,
  columns,
  renderFns,
  renderFnsTextProps,
  multiselect,
  responsiveness,
  isSelected,
  toggleItemSelected,
  rowHeight,
  rowStyles,
  onRowClick,
  onRowHover,
  rowClickLink,
  rowLinkOpenInNewTab,
  overflowMenu,
  disableRowClickOnColumns,
  isSkeletonState,
  activeColumn,
  isSidebarSelected,
  horizontalScroll,
  stickyFirstColumn,
  columnWidths,
  checkboxOuterWidth = 0,
  trailingEmptyColumnWidth = 0,
  columnSizing,
  stickyScrollEdgeShadow,
}: TableRowProps<T, K>): React.ReactElement {
  const onClick = useMemo(() => {
    if (onRowClick) {
      return (e: React.MouseEvent<HTMLDivElement>) => {
        onRowClick(item, e as React.MouseEvent<HTMLDivElement>);
      };
    }
    return undefined;
  }, [item, onRowClick]);
  const isMultiSelectDisabled = useMemo(() => {
    return multiselect?.disableMultiSelectForItem?.(item) || false;
  }, [item, multiselect]);
  const menu = useMemo(
    () => (overflowMenu ? overflowMenu(item) : undefined),
    [item, overflowMenu],
  );
  const linkUrl: Route | null | undefined = useMemo(() => {
    if (rowClickLink) {
      return rowClickLink(item);
    } else {
      return null;
    }
  }, [item, rowClickLink]);

  // Clickable rows show a hover fill; selected (checkbox) and sidebar-open
  // rows keep their own fill.
  const showRowHover =
    !isSkeletonState &&
    !isSelected &&
    !isSidebarSelected &&
    !!(onClick || linkUrl);
  // Sticky cells need an opaque surface, so the row's alpha state fill is
  // layered over bg-primary as a flat gradient.
  const stickyRowSurface = cn(
    "bg-bg-primary",
    isSelected && STICKY_SELECTED_SURFACE,
    isSidebarSelected && !isSelected && STICKY_SIDEBAR_SELECTED_SURFACE,
    showRowHover && STICKY_HOVER_SURFACE,
  );

  return (
    <div
      className="flex self-stretch"
      data-testid={isSkeletonState ? undefined : "table-row"}
      onMouseEnter={
        onRowHover && !isSkeletonState ? () => onRowHover(item) : undefined
      }
    >
      <div
        className={cn(
          "group/row m-0 flex flex-1 border-b border-border-primary bg-transparent p-0",
          isSelected && "bg-state-selected",
          isSidebarSelected && !isSelected && "bg-state-pressed",
          showRowHover && "hover:bg-state-hover",
        )}
        style={{
          userSelect: "none",
          height: typeof rowHeight === "number" ? rowHeight : undefined,
          minHeight: 42,
          ...rowStyles,
        }}
      >
        {multiselect ? (
          <TableContentCell
            width={multiselect.checkboxColumnWidth}
            minWidth={multiselect.checkboxColumnWidth}
            alignContent={"Center"}
            layoutFixed={horizontalScroll}
            flex={horizontalScroll ? 0 : undefined}
            stickyLeft={horizontalScroll && stickyFirstColumn ? 0 : undefined}
            stickyZIndex={
              horizontalScroll && stickyFirstColumn ? 12 : undefined
            }
            stickySurfaceClassName={stickyRowSurface}
            className={cn(horizontalScroll && "!border-r-0 !shadow-none")}
          >
            {!isSkeletonState && (
              <div
                onClick={(e) => {
                  if (!isMultiSelectDisabled && toggleItemSelected) {
                    toggleItemSelected(item, e.shiftKey);
                  }
                }}
              >
                <Checkbox
                  checked={isSelected || false}
                  disabled={isMultiSelectDisabled}
                />
              </div>
            )}
          </TableContentCell>
        ) : (
          <div
            aria-hidden="true"
            className={cn(
              "shrink-0",
              horizontalScroll && stickyFirstColumn && "sticky left-0 z-[12]",
              horizontalScroll && stickyFirstColumn && stickyRowSurface,
            )}
            style={{ width: LEADING_ROW_GUTTER }}
          />
        )}
        {columns.map((column, index) => {
          const responsivenessConfig = responsiveness?.[column];
          const innerW = horizontalScroll
            ? columnWidths?.[column]
            : responsivenessConfig?.width;
          const innerMin = horizontalScroll
            ? responsivenessConfig?.minWidth ?? columnWidths?.[column]
            : responsivenessConfig?.minWidth;
          return (
            <TableContentCell
              key={column}
              dataColumn={horizontalScroll ? String(column) : undefined}
              width={innerW}
              minWidth={innerMin}
              flex={horizontalScroll ? 0 : responsivenessConfig?.flex}
              layoutFixed={horizontalScroll}
              flexSizing={horizontalScroll ? columnSizing?.[column] : undefined}
              stickyLeft={
                horizontalScroll && stickyFirstColumn && index === 0
                  ? checkboxOuterWidth
                  : undefined
              }
              stickyZIndex={
                horizontalScroll && stickyFirstColumn && index === 0
                  ? 11
                  : undefined
              }
              stickyScrollEdgeShadow={
                !!(
                  stickyScrollEdgeShadow &&
                  horizontalScroll &&
                  stickyFirstColumn &&
                  index === 0
                )
              }
              stickySurfaceClassName={stickyRowSurface}
              wrapHeight={
                typeof rowHeight === "number" ? false : rowHeight.wrap
              }
              linkUrl={
                disableRowClickOnColumns && disableRowClickOnColumns.has(column)
                  ? undefined
                  : linkUrl
              }
              linkOpenInNewTab={rowLinkOpenInNewTab}
              onClick={
                disableRowClickOnColumns && disableRowClickOnColumns.has(column)
                  ? undefined
                  : onClick
              }
              active={activeColumn === column}
              textProps={renderFnsTextProps?.[column]}
            >
              {renderFns[column]?.(item)}
            </TableContentCell>
          );
        })}
        {overflowMenu != null ? (
          <TableContentCell
            width={OVERFLOW_MENU_COLUMN_WIDTH}
            minWidth={OVERFLOW_MENU_COLUMN_WIDTH}
            alignContent={"Left"}
            layoutFixed={horizontalScroll}
            flex={horizontalScroll ? 0 : undefined}
            stickyRight={horizontalScroll ? 0 : undefined}
            stickyZIndex={horizontalScroll ? 10 : undefined}
            stickyTransparent={horizontalScroll}
            className={cn(horizontalScroll && "ml-auto")}
          >
            {menu ? (
              <TableRowOverflow menu={menu} showOverflow={!isSkeletonState} />
            ) : (
              <></>
            )}
          </TableContentCell>
        ) : null}
        {horizontalScroll && (
          <div
            aria-hidden="true"
            className="shrink-0"
            style={{
              width: trailingEmptyColumnWidth,
            }}
          />
        )}
      </div>
    </div>
  );
}

type TableHeaderCellProps<T, K extends string> = {
  columnTitles: Record<K, string>;
  multiselect?: MultiSelectConfig<T>;
  responsiveness?: ResponsivenessConfigs<K>;
  selectedSort?: SortConfig<K>;
  setSelectedSort?: (x: SortConfig<K>) => void;
  allFilteredItemsSelected?: boolean;
  toggleAllFilteredItemsSelected?: () => void;
  includeOverflowMenuColumn?: boolean;
  stickyPositioning?: number;
  isSkeletonState?: boolean;
  disabledSorts?: K[];
  headerCellStyles?: HeaderCellStyles<K>;
  onColumnHeaderClick?: (column: K) => void;
  headerHeight?: RowHeight;
  showSortDirection?: boolean;
  headerTooltipCopy?: HeaderTooltipCopy<K>;
  multiline?: boolean;
  wrapHeight?: boolean;
  column: K;
  horizontalScroll?: boolean;
  /** Inner width (TableContentCell `width` prop) when `horizontalScroll`. */
  columnWidthInner?: number;
  stickyLeft?: number;
  stickyZIndex?: number;
  resizableColumns?: boolean;
  /**
   * Called on pointer-up with the final width (in inner-width units). Writes
   * to persistence atomically — there is no separate commit step.
   */
  onColumnResizeCommit?: (column: K, width: number) => void;
  minColumnWidth?: number;
  /** Horizontal-scroll flex sizing of this column (see `computeTableColumnSizing`). */
  flexSizing?: TableColumnFlexSizing;
  stickyScrollEdgeShadow?: boolean;
};

export function TableHeaderCell<T, K extends string>(
  props: TableHeaderCellProps<T, K>,
): React.ReactElement {
  const {
    column,
    responsiveness,
    headerCellStyles,
    isSkeletonState,
    disabledSorts,
    onColumnHeaderClick,
    columnTitles,
    headerTooltipCopy,
    selectedSort,
    setSelectedSort,
    showSortDirection = true,
    wrapHeight,
    multiline = false,
    horizontalScroll,
    columnWidthInner,
    stickyLeft,
    stickyZIndex,
    resizableColumns,
    onColumnResizeCommit,
    minColumnWidth,
    flexSizing,
    stickyScrollEdgeShadow,
  } = props;
  const wasResizingRef = useRef(false);
  const onClickHeader = useCallback(() => {
    if (wasResizingRef.current) {
      wasResizingRef.current = false;
      return;
    }
    if (onColumnHeaderClick) {
      onColumnHeaderClick(column);
    }
    if (!setSelectedSort || disabledSorts?.includes(column)) {
      return;
    }
    if (selectedSort?.column === column) {
      setSelectedSort({
        column: column,
        descending: !selectedSort?.descending,
      });
    } else {
      setSelectedSort({
        column: column,
        descending: true,
      });
    }
  }, [
    onColumnHeaderClick,
    setSelectedSort,
    disabledSorts,
    column,
    selectedSort,
  ]);
  const [isHovered, hoverBind] = useHover();
  const showSortArrow = selectedSort?.column === column && showSortDirection;
  const columnSortDisabled = disabledSorts?.includes(column);
  const onColumnResizeCommitRef = useRef(onColumnResizeCommit);
  useEffect(() => {
    onColumnResizeCommitRef.current = onColumnResizeCommit;
  }, [onColumnResizeCommit]);
  const [isResizing, setIsResizing] = useState(false);
  const columnWidthInnerRef = useRef(columnWidthInner);
  useEffect(() => {
    columnWidthInnerRef.current = columnWidthInner;
  }, [columnWidthInner]);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!horizontalScroll || !resizableColumns || !onColumnResizeCommit)
        return;
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      wasResizingRef.current = true;
      setIsResizing(true);

      const floor =
        minColumnWidth ??
        responsiveness?.[column]?.minWidth ??
        MIN_TABLE_COLUMN_WIDTH;
      const startX = e.clientX;
      // The stored width is the column's flex basis; a growing column renders
      // wider than that. Start the drag from the rendered width so the handle
      // stays under the cursor, and pin the column to the dragged width (the
      // committed column is then fixed; see `computeTableColumnSizing`).
      const storedWidth =
        columnWidthInnerRef.current ??
        responsiveness?.[column]?.minWidth ??
        DEFAULT_TABLE_COLUMN_WIDTH;
      const startingEl = e.currentTarget as HTMLElement;
      const headerCell = startingEl.closest<HTMLElement>("[data-column]");
      const renderedWidth = headerCell
        ? headerCell.getBoundingClientRect().width - PADDING_LEFT
        : storedWidth;
      const startWidth = Math.max(floor, Math.round(renderedWidth));

      // Find the inner table wrapper and all cells in this column for live
      // DOM-level width updates during the drag. Committing to React state only
      // on pointerup keeps the drag smooth even under heavy re-render load.
      const columnKey = String(column);
      const innerWrapper = startingEl.closest<HTMLElement>(
        "[data-table-inner='true']",
      );
      const cells = innerWrapper
        ? Array.from(
            innerWrapper.querySelectorAll<HTMLElement>(
              `[data-column="${CSS.escape(columnKey)}"]`,
            ),
          )
        : [];
      // Restore these inline values after the commit so the props React
      // doesn't re-write (it diffs props, not the DOM) don't keep drag values.
      const DRAG_STYLE_PROPS = [
        "flexGrow",
        "flexBasis",
        "width",
        "minWidth",
        "maxWidth",
      ] as const;
      const savedCellStyles = cells.map((cell) =>
        DRAG_STYLE_PROPS.map((prop) => cell.style[prop]),
      );
      // Pinning this column can leave nothing to absorb the slack (e.g. a
      // `growableColumn` table); then the last other data column stands in
      // during the drag, as `computeTableColumnSizing` will after the commit.
      const otherHeaderCells = Array.from(
        headerCell?.parentElement?.children ?? [],
      ).filter(
        (el): el is HTMLElement =>
          el instanceof HTMLElement &&
          el.dataset.column != null &&
          el.dataset.column !== columnKey,
      );
      const standInKey = otherHeaderCells.some(
        (el) => Number(el.style.flexGrow) > 0 && !el.style.maxWidth,
      )
        ? undefined
        : otherHeaderCells[otherHeaderCells.length - 1]?.dataset.column;
      const standInCells =
        innerWrapper && standInKey != null
          ? Array.from(
              innerWrapper.querySelectorAll<HTMLElement>(
                `[data-column="${CSS.escape(standInKey)}"]`,
              ),
            )
          : [];
      const savedStandInStyles = standInCells.map(
        (cell) => [cell.style.flexGrow, cell.style.maxWidth] as const,
      );
      const savedWrapperMinWidth = innerWrapper?.style.minWidth ?? "";
      const startWrapperMinWidth = innerWrapper
        ? parseFloat(innerWrapper.style.minWidth) || innerWrapper.offsetWidth
        : 0;

      const prevBodyUserSelect = document.body.style.userSelect;
      const prevBodyCursor = document.body.style.cursor;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      let latestWidth = startWidth;
      let moved = false;
      let rafId: number | null = null;
      const applyDom = (inner: number) => {
        const outer = `${inner + PADDING_LEFT}px`;
        for (const cell of cells) {
          cell.style.flexGrow = "0";
          cell.style.flexBasis = outer;
          cell.style.width = outer;
          cell.style.minWidth = outer;
          cell.style.maxWidth = outer;
        }
        for (const cell of standInCells) {
          if (!(Number(cell.style.flexGrow) > 0)) cell.style.flexGrow = "1";
          cell.style.maxWidth = "";
        }
        if (innerWrapper) {
          // The wrapper's min width is the sum of column bases; this column's
          // basis goes from its stored width to the dragged width.
          const newWrapper = startWrapperMinWidth + (inner - storedWidth);
          innerWrapper.style.minWidth = `${newWrapper}px`;
        }
      };

      const handleMove = (ev: PointerEvent | MouseEvent) => {
        ev.preventDefault();
        const delta = ev.clientX - startX;
        moved = moved || delta !== 0;
        latestWidth = Math.max(floor, startWidth + delta);
        if (rafId == null) {
          rafId = window.requestAnimationFrame(() => {
            rafId = null;
            applyDom(latestWidth);
          });
        }
      };
      const cleanup = () => {
        window.removeEventListener("pointermove", handleMove, true);
        window.removeEventListener("pointerup", handleUp, true);
        window.removeEventListener("pointercancel", handleUp, true);
        window.removeEventListener("mousemove", handleMove, true);
        window.removeEventListener("mouseup", handleUp, true);
        window.removeEventListener("blur", handleUp, true);
        if (rafId != null) window.cancelAnimationFrame(rafId);
        document.body.style.userSelect = prevBodyUserSelect;
        document.body.style.cursor = prevBodyCursor;
        setIsResizing(false);
        // Hand the inline styles back to React, then commit synchronously so
        // React writes the new sizing before the next paint (no flicker). A
        // bare click on the handle doesn't count as a resize.
        cells.forEach((cell, i) => {
          DRAG_STYLE_PROPS.forEach((prop, j) => {
            cell.style[prop] = savedCellStyles[i][j];
          });
        });
        standInCells.forEach((cell, i) => {
          [cell.style.flexGrow, cell.style.maxWidth] = savedStandInStyles[i];
        });
        if (innerWrapper) innerWrapper.style.minWidth = savedWrapperMinWidth;
        if (moved) {
          flushSync(() => {
            onColumnResizeCommitRef.current?.(column, latestWidth);
          });
        }
        window.setTimeout(() => {
          wasResizingRef.current = false;
        }, 0);
      };
      const handleUp = () => {
        cleanup();
      };

      window.addEventListener("pointermove", handleMove, true);
      window.addEventListener("pointerup", handleUp, true);
      window.addEventListener("pointercancel", handleUp, true);
      window.addEventListener("mousemove", handleMove, true);
      window.addEventListener("mouseup", handleUp, true);
      window.addEventListener("blur", handleUp, true);
    },
    [
      column,
      horizontalScroll,
      minColumnWidth,
      onColumnResizeCommit,
      resizableColumns,
      responsiveness,
    ],
  );

  const innerW = horizontalScroll
    ? columnWidthInner
    : responsiveness?.[column]?.width;
  const innerMin = horizontalScroll
    ? minColumnWidth ?? columnWidthInner ?? responsiveness?.[column]?.minWidth
    : responsiveness?.[column]?.minWidth;

  return (
    <TableContentCell
      key={column}
      dataColumn={horizontalScroll ? String(column) : undefined}
      width={innerW}
      minWidth={innerMin}
      flex={horizontalScroll ? 0 : responsiveness?.[column]?.flex}
      layoutFixed={horizontalScroll}
      flexSizing={horizontalScroll ? flexSizing : undefined}
      stickyLeft={stickyLeft}
      stickyZIndex={stickyZIndex}
      stickyScrollEdgeShadow={stickyScrollEdgeShadow}
      stickySurfaceClassName="bg-bg-primary"
      style={headerCellStyles?.[column]?.outer}
      innerStyle={headerCellStyles?.[column]?.inner}
      wrapHeight={wrapHeight}
      onClick={isSkeletonState ? undefined : onClickHeader}
      suffix={
        horizontalScroll && resizableColumns && onColumnResizeCommit ? (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${String(column)} column`}
            className="group absolute top-0 -right-1 z-30 flex h-full w-2 cursor-col-resize touch-none items-stretch justify-center select-none"
            onPointerDown={onResizePointerDown}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <span
              className={cn(
                "my-1 w-0.5 rounded-full bg-action-primary opacity-0 transition-opacity group-hover:opacity-100",
                isResizing && "opacity-100",
              )}
            />
          </div>
        ) : undefined
      }
    >
      <div
        className={cn(
          "flex items-center cursor-pointer gap-1",
          disabledSorts?.includes(column) &&
            !onColumnHeaderClick &&
            "cursor-default",
        )}
        {...hoverBind}
      >
        <Text
          variant="compact-title"
          multiline={multiline}
          className="translate-y-px text-text-tertiary"
          style={{
            flex: 1,
          }}
        >
          {columnTitles[column]}
        </Text>
        {headerTooltipCopy?.[column] && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleQuestionOutlineIcon className="mt-0.5 size-4 text-icon-tertiary" />
            </TooltipTrigger>
            <TooltipContent>{headerTooltipCopy[column]}</TooltipContent>
          </Tooltip>
        )}
        {showSortArrow && selectedSort?.descending && (
          <ArrowDownIcon className="size-4 text-icon-tertiary" />
        )}
        {showSortArrow && !selectedSort?.descending && (
          <ArrowUpIcon className="size-4 text-icon-tertiary" />
        )}
        {!showSortArrow && isHovered && !columnSortDisabled && (
          <ArrowDownIcon className="size-4 text-icon-tertiary" />
        )}
      </div>
    </TableContentCell>
  );
}

type TableHeaderRowProps<T, K extends string> = {
  columns: K[];
  columnTitles: Record<K, string>;
  multiselect?: MultiSelectConfig<T>;
  responsiveness?: ResponsivenessConfigs<K>;
  selectedSort?: SortConfig<K>;
  setSelectedSort?: (x: SortConfig<K>) => void;
  allFilteredItemsSelected?: boolean;
  toggleAllFilteredItemsSelected?: () => void;
  includeOverflowMenuColumn?: boolean;
  stickyPositioning?: number;
  isSkeletonState?: boolean;
  disabledSorts?: K[];
  headerCellStyles?: HeaderCellStyles<K>;
  onColumnHeaderClick?: (column: K) => void;
  activeColumn?: K;
  headerHeight?: RowHeight;
  showSortDirection?: boolean;
  headerTooltipCopy?: HeaderTooltipCopy<K>;
  multiline?: boolean;
  horizontalScroll?: boolean;
  stickyFirstColumn?: boolean;
  resizableColumns?: boolean;
  columnWidths?: Record<K, number>;
  onColumnResizeCommit?: (column: K, width: number) => void;
  checkboxOuterWidth?: number;
  /**
   * Fixed empty gutter after the last column (after "···"). It never grows:
   * the data columns absorb leftover space (see `columnSizing`).
   */
  trailingEmptyColumnWidth?: number;
  /**
   * Horizontal-scroll flex sizing per data column, from
   * `computeTableColumnSizing`. Header, body rows and skeleton share it.
   */
  columnSizing?: Partial<Record<K, TableColumnFlexSizing>>;
  /** Last sticky column shows a shadow when the table body is scrolled horizontally. */
  stickyScrollEdgeShadow?: boolean;
};

export function TableHeaderRow<T, K extends string>(
  props: TableHeaderRowProps<T, K>,
): React.ReactElement {
  const {
    columns,
    multiselect,
    allFilteredItemsSelected,
    toggleAllFilteredItemsSelected,
    includeOverflowMenuColumn,
    stickyPositioning = 0,
    isSkeletonState,
    headerHeight,
    horizontalScroll,
    stickyFirstColumn,
    resizableColumns,
    columnWidths,
    onColumnResizeCommit,
    checkboxOuterWidth = 0,
    trailingEmptyColumnWidth = 0,
    columnSizing,
    stickyScrollEdgeShadow,
  } = props;
  const onClickCheckbox = useCallback(() => {
    if (toggleAllFilteredItemsSelected) {
      toggleAllFilteredItemsSelected();
    }
  }, [toggleAllFilteredItemsSelected]);
  return (
    <div className="flex self-stretch">
      <div
        className={cn(
          "sticky z-[9] flex flex-1 flex-nowrap border-b border-border-primary bg-transparent",
        )}
        style={{
          marginTop: 12,
          paddingBottom: 0,
          height:
            typeof headerHeight === "number" ? headerHeight || 36 : undefined,
          minHeight: 32,
          userSelect: "none",
          top: stickyPositioning,
        }}
      >
        {multiselect ? (
          <TableContentCell
            width={multiselect.checkboxColumnWidth}
            minWidth={multiselect.checkboxColumnWidth}
            alignContent={"Center"}
            layoutFixed={horizontalScroll}
            flex={horizontalScroll ? 0 : undefined}
            stickyLeft={horizontalScroll && stickyFirstColumn ? 0 : undefined}
            stickyZIndex={
              horizontalScroll && stickyFirstColumn ? 22 : undefined
            }
            stickySurfaceClassName="bg-bg-primary"
            className={cn(horizontalScroll && "!border-r-0 !shadow-none")}
          >
            <Checkbox
              checked={allFilteredItemsSelected || false}
              onCheckedChange={onClickCheckbox}
              disabled={isSkeletonState}
            />
          </TableContentCell>
        ) : (
          <div
            aria-hidden="true"
            className={cn(
              "shrink-0",
              horizontalScroll &&
                stickyFirstColumn &&
                "sticky left-0 z-[22] bg-bg-primary",
            )}
            style={{ width: LEADING_ROW_GUTTER }}
          />
        )}
        {columns.map((column, index) => (
          <TableHeaderCell
            key={column}
            {...props}
            column={column}
            horizontalScroll={horizontalScroll}
            columnWidthInner={columnWidths?.[column]}
            minColumnWidth={props.responsiveness?.[column]?.minWidth}
            stickyLeft={
              horizontalScroll && stickyFirstColumn && index === 0
                ? checkboxOuterWidth
                : undefined
            }
            stickyZIndex={
              horizontalScroll && stickyFirstColumn && index === 0
                ? 21
                : undefined
            }
            stickyScrollEdgeShadow={
              !!(
                stickyScrollEdgeShadow &&
                horizontalScroll &&
                stickyFirstColumn &&
                index === 0
              )
            }
            resizableColumns={resizableColumns}
            onColumnResizeCommit={onColumnResizeCommit}
            flexSizing={columnSizing?.[column]}
            wrapHeight={
              typeof headerHeight === "number"
                ? false
                : headerHeight?.wrap || false
            }
          />
        ))}
        {includeOverflowMenuColumn && (
          <TableContentCell
            defaultEmptyTextToDash={false}
            width={OVERFLOW_MENU_COLUMN_WIDTH}
            minWidth={OVERFLOW_MENU_COLUMN_WIDTH}
            layoutFixed={horizontalScroll}
            flex={horizontalScroll ? 0 : undefined}
            stickyRight={horizontalScroll ? 0 : undefined}
            stickyZIndex={horizontalScroll ? 20 : undefined}
            stickyTransparent={horizontalScroll}
            className={cn(horizontalScroll && "ml-auto")}
          />
        )}
        {horizontalScroll && (
          <div
            aria-hidden="true"
            className="shrink-0"
            style={{
              width: trailingEmptyColumnWidth,
            }}
          />
        )}
      </div>
    </div>
  );
}
